import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { getTradingDefaults, updateTradingDefaults } from '../../api/account';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import DWLFLoader from '../../components/DWLFLoader';

const ALLOWED_CURRENCIES = ['USD', 'GBP', 'EUR'];

const CURRENCY_SYMBOL = {
  USD: '$',
  GBP: '£',
  EUR: '€',
};

// Backend stores defaultRiskPerTrade as a decimal in (0,1]; the form
// shows it as a percentage so users type "1" for 1%, not "0.01". These
// converters keep the boundary explicit.
const decimalToPercent = (decimal) =>
  decimal == null || !Number.isFinite(decimal) ? '' : (decimal * 100).toString();

const percentInputToDecimal = (percentString) => {
  const trimmed = (percentString ?? '').toString().trim();
  if (trimmed === '') {return null;}
  const num = Number(trimmed);
  if (!Number.isFinite(num)) {return NaN;}
  return num / 100;
};

const TradingDefaultsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Form state — stored as the form's string representation so partial typing
  // ("123.") works without forcing intermediate parse. Values are converted
  // at save time.
  const [accountBalance, setAccountBalance] = useState('');
  const [accountCurrency, setAccountCurrency] = useState('USD');
  const [riskPercent, setRiskPercent] = useState('');
  const [maxConcurrentTrades, setMaxConcurrentTrades] = useState('');

  const fetchDefaults = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await getTradingDefaults();
      const td = data?.tradingDefaults || {};
      setAccountBalance(td.accountBalance != null ? td.accountBalance.toString() : '');
      setAccountCurrency(td.accountCurrency || 'USD');
      setRiskPercent(decimalToPercent(td.defaultRiskPerTrade));
      setMaxConcurrentTrades(td.maxConcurrentTrades != null ? td.maxConcurrentTrades.toString() : '');
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'Failed to load trading defaults.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const validate = () => {
    const errs = {};

    const balanceNum = Number(accountBalance);
    if (accountBalance === '' || !Number.isFinite(balanceNum) || balanceNum <= 0) {
      errs.accountBalance = 'Must be a positive number';
    }

    if (!ALLOWED_CURRENCIES.includes(accountCurrency)) {
      errs.accountCurrency = `Must be one of ${ALLOWED_CURRENCIES.join(', ')}`;
    }

    const riskDecimal = percentInputToDecimal(riskPercent);
    if (
      riskDecimal == null ||
      !Number.isFinite(riskDecimal) ||
      riskDecimal <= 0 ||
      riskDecimal > 1
    ) {
      errs.defaultRiskPerTrade = 'Must be a percentage in (0, 100]';
    }

    const maxNum = Number(maxConcurrentTrades);
    if (
      maxConcurrentTrades === '' ||
      !Number.isInteger(maxNum) ||
      maxNum < 1 ||
      maxNum > 20
    ) {
      errs.maxConcurrentTrades = 'Must be a whole number from 1 to 20';
    }

    return errs;
  };

  const handleSave = async () => {
    setSaveError(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {return;}

    try {
      setSaving(true);
      const payload = {
        accountBalance: Number(accountBalance),
        accountCurrency,
        defaultRiskPerTrade: percentInputToDecimal(riskPercent),
        maxConcurrentTrades: Number(maxConcurrentTrades),
      };
      const data = await updateTradingDefaults(payload);
      // Re-sync state from server response so any backend-side coercion
      // (e.g. trimmed precision) is reflected back in the form.
      const td = data?.tradingDefaults;
      if (td) {
        setAccountBalance(td.accountBalance.toString());
        setAccountCurrency(td.accountCurrency);
        setRiskPercent(decimalToPercent(td.defaultRiskPerTrade));
        setMaxConcurrentTrades(td.maxConcurrentTrades.toString());
      }
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(extractErrorMessage(err, 'Failed to save trading defaults.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <DWLFLoader size="medium" />
        </Box>
      </Paper>
    );
  }

  const currencySymbol = CURRENCY_SYMBOL[accountCurrency] || '';
  const balanceNumForPreview = Number(accountBalance);
  const riskDecimalForPreview = percentInputToDecimal(riskPercent);
  const showRiskPreview =
    Number.isFinite(balanceNumForPreview) &&
    balanceNumForPreview > 0 &&
    Number.isFinite(riskDecimalForPreview) &&
    riskDecimalForPreview > 0 &&
    riskDecimalForPreview <= 1;
  const riskPerTradePreview = showRiskPreview
    ? balanceNumForPreview * riskDecimalForPreview
    : null;

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Trading Defaults
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Account size and risk settings used to position-size every strategy
            alert and planned trade. Update these as your account grows or shrinks.
          </Typography>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
            {loadError}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
          <TextField
            label="Account balance"
            type="number"
            value={accountBalance}
            onChange={(e) => setAccountBalance(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.accountBalance)}
            helperText={
              fieldErrors.accountBalance ||
              'The total capital you allocate to this strategy account. Used to size positions.'
            }
            InputProps={{
              startAdornment: currencySymbol ? (
                <InputAdornment position="start">{currencySymbol}</InputAdornment>
              ) : null,
              inputProps: { min: 0, step: 'any' },
            }}
            fullWidth
          />

          <FormControl fullWidth error={Boolean(fieldErrors.accountCurrency)}>
            <InputLabel>Account currency</InputLabel>
            <Select
              label="Account currency"
              value={accountCurrency}
              onChange={(e) => setAccountCurrency(e.target.value)}
              disabled={saving}
            >
              {ALLOWED_CURRENCIES.map((ccy) => (
                <MenuItem key={ccy} value={ccy}>
                  {CURRENCY_SYMBOL[ccy]} {ccy}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.accountCurrency && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {fieldErrors.accountCurrency}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Default risk per trade"
            type="number"
            value={riskPercent}
            onChange={(e) => setRiskPercent(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.defaultRiskPerTrade)}
            helperText={
              fieldErrors.defaultRiskPerTrade ||
              (showRiskPreview
                ? `≈ ${currencySymbol}${riskPerTradePreview.toFixed(2)} risked per signal at this size`
                : 'Percentage of account balance risked on each strategy fire. 1% is a common starting point.')
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0, max: 100, step: 'any' },
            }}
            fullWidth
          />

          <TextField
            label="Max concurrent trades"
            type="number"
            value={maxConcurrentTrades}
            onChange={(e) => setMaxConcurrentTrades(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.maxConcurrentTrades)}
            helperText={
              fieldErrors.maxConcurrentTrades ||
              'Cap on simultaneously-open planned + open trades. Whole number 1–20.'
            }
            InputProps={{ inputProps: { min: 1, max: 20, step: 1 } }}
            fullWidth
          />

          <Box>
            <Button
              variant="contained"
              startIcon={saving ? <DWLFLoader size="tiny" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(savedAt)}
        autoHideDuration={3000}
        onClose={() => setSavedAt(null)}
      >
        <Alert
          onClose={() => setSavedAt(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Trading defaults saved.
        </Alert>
      </Snackbar>
    </>
  );
};

export default TradingDefaultsTab;
